"use client";

import { useState } from "react";
import useSWR from "swr";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { Plus, X, Tag, Check } from "lucide-react";
import { apiClient } from "@/lib/api-client";

interface DocCollection {
  itemId: string;
  collectionId: string;
  name: string;
  icon: string | null;
}

interface CollectionOption {
  id: string;
  name: string;
  icon: string | null;
}

const fetcher = <T,>(url: string): Promise<T> =>
  apiClient.get<T>(url).then((r) => r.data);

interface DocumentTagsProps {
  docId: string;
  /**
   * Render for the title bar rather than as its own row: no border or block
   * padding, and badges stay on one line (the title bar cannot grow taller).
   */
  inline?: boolean;
}

export function DocumentTags({ docId, inline = false }: DocumentTagsProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  const {
    data: docCollections,
    mutate: mutateDocCollections,
  } = useSWR<DocCollection[]>(`/api/documents/${docId}/collections`, fetcher);

  const { data: allCollections } = useSWR<CollectionOption[]>(
    menuOpen ? "/api/collections" : null,
    fetcher
  );

  const handleRemove = async (collectionId: string) => {
    await apiClient.request(`/api/documents/${docId}/collections`, {
      method: "DELETE",
      body: { collectionId },
    });
    mutateDocCollections();
  };

  const handleAdd = async (collectionId: string) => {
    await apiClient.post(`/api/documents/${docId}/collections`, {
      collectionId,
    });
    mutateDocCollections();
  };

  const currentIds = new Set(
    (docCollections ?? []).map((c) => c.collectionId)
  );

  if (!docCollections) return null;

  return (
    <div
      className={
        inline
          // This group yields width BEFORE the title does: `shrink` with a
          // `min-w-0` floor lets it collapse to nothing, while the title keeps a
          // hard `min-w-[6rem]`. Badges clip inside `overflow-hidden` rather
          // than wrapping (the row cannot grow taller) or pushing the controls
          // on the right off the end.
          ? "flex shrink items-center gap-1.5 min-w-0 overflow-hidden"
          : "flex items-center gap-1.5 px-4 py-1.5 border-b min-h-[32px] flex-wrap"
      }
    >
      <Tag className="h-3 w-3 text-muted-foreground shrink-0" />

      {docCollections.map((c) => (
        <Badge
          key={c.collectionId}
          variant="secondary"
          className="text-[11px] gap-1 pl-1.5 pr-0.5 py-0 h-5 font-normal max-w-[10rem] shrink-0"
        >
          {c.icon && <span className="mr-0.5">{c.icon}</span>}
          <span className="truncate">{c.name}</span>
          <button
            type="button"
            className="ml-0.5 rounded-sm hover:bg-muted-foreground/20 p-0.5"
            onClick={() => handleRemove(c.collectionId)}
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </Badge>
      ))}

      {docCollections.length === 0 && !inline && (
        <span className="text-[11px] text-muted-foreground">
          No collections
        </span>
      )}

      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-5 w-5 shrink-0"
          >
            <Plus className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-52">
          {allCollections && allCollections.length > 0 ? (
            allCollections.map((col) => {
              const isActive = currentIds.has(col.id);
              return (
                <DropdownMenuItem
                  key={col.id}
                  onClick={(e) => {
                    e.preventDefault();
                    if (isActive) {
                      handleRemove(col.id);
                    } else {
                      handleAdd(col.id);
                    }
                  }}
                >
                  <span className="w-4 shrink-0">
                    {isActive ? (
                      <Check className="h-3.5 w-3.5 text-primary" />
                    ) : col.icon ? (
                      <span className="text-xs">{col.icon}</span>
                    ) : null}
                  </span>
                  <span className="truncate">{col.name}</span>
                </DropdownMenuItem>
              );
            })
          ) : (
            <p className="text-xs text-muted-foreground p-2">
              No collections yet
            </p>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
